import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Lock, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Input } from '../../Components/ui';

// Tela de login. Validação de formato feita com zod (via react-hook-form);
// erros de credenciais/pendência de aprovação vêm da API e são tratados no catch abaixo.
const loginSchema = z.object({
  email: z.string().email('Email inválido'),
  senha: z.string().min(1, 'Senha é obrigatória'),
});

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [erro, setErro] = useState('');
  const [mensagem, setMensagem] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(loginSchema),
  });

  // Mostra uma mensagem de sucesso vinda de outra tela (ex.: "cadastro realizado,
  // aguarde aprovação" após o Registrar) e limpa o state da rota para que a
  // mensagem não reapareça se o usuário der refresh na página.
  useEffect(() => {
    if (location.state?.mensagem) {
      setMensagem(location.state.mensagem);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const onSubmit = async (data) => {
    setErro('');
    try {
      await login(data.email, data.senha);
      navigate('/selecionar', { replace: true });
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData?.status === 'pendente') {
        setErro('Cadastro pendente de aprovação. Aguarde liberação do master.');
      } else {
        setErro(errorData?.error || 'Não foi possível entrar. Verifique seus dados.');
      }
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-[#F9FAFB] to-[#E5E7EB] dark:from-gray-950 dark:to-gray-900 p-4">
      <div className="w-full max-w-md bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-3xl border border-white/50 dark:border-gray-800/50 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] p-4">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo.svg" alt="Portal Novas Histórias" className="w-20 h-auto object-contain mb-4" />
          <h1
            className="text-2xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight"
            translate="no"
          >
            Portal Novas Histórias
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Controle de Presença</p>
        </div>

        {mensagem && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-900/40 text-green-700 dark:text-green-400 text-sm mb-4">
            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{mensagem}</span>
          </div>
        )}
        {erro && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 text-red-600 dark:text-red-400 text-sm mb-4">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{erro}</span>
          </div>
        )}

        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)} autoComplete="on">
          <Input
            label="Usuário"
            type="email"
            size="lg"
            icon={Mail}
            {...register('email')}
            autoComplete="email"
            placeholder="seu@email.com"
            error={!!errors.email}
            errorMessage={errors.email?.message}
          />

          <Input
            label="Senha"
            type="password"
            size="lg"
            icon={Lock}
            {...register('senha')}
            autoComplete="current-password"
            placeholder="••••••••"
            error={!!errors.senha}
            errorMessage={errors.senha?.message}
          />

          <div className="text-right">
            <Link
              to="/esqueci-senha"
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors"
            >
              Esqueci minha senha
            </Link>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-11 mt-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Entrando...
              </>
            ) : (
              'Entrar'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/registrar"
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors"
          >
            Criar uma conta
          </Link>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-800 text-center text-xs text-gray-400 dark:text-gray-500">
          © 2026 Portal Novas Histórias
        </div>
      </div>
    </div>
  );
}

export default Login;
